
import React, { useState, useEffect } from "react";
import { Typography, Grid, Card, Avatar, Button } from "@mui/material";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import { colors } from "../components/Theme/WebTheme";
import CircularLoading from "../components/CircularLoading";
import FileUploadIcon from "@mui/icons-material/FileUpload";
import AlertDialog from '../components/AlertDialog';
import ImageViewerModal from '../components/ImageViewerModal';
import { api } from "common";
import { makeStyles } from "@mui/styles";

const useStyles = makeStyles((theme) => ({
  card: {
    borderRadius: "10px",
    backgroundColor: colors.SECONDARY,
    minHeight: 60,
    minWidth: 300,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    textAlign: "center",
    padding: 2,
    marginBottom: 10,
    boxShadow: `0px 2px 5px ${colors.MAIN_COLOR}`,
  },
  txt: {
    padding: 10,
    fontWeight: "bold",
    minHeight: 60,
    backgroundColor:colors.SECONDARY,
    color: colors.LandingPage_Background,
    boxShadow: 3,
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  },
  buttonStyle:{
    borderRadius: "19px",
    minHeight: 50,
    minWidth: 150,
    marginBottom: 20,
    marginTop: 20,
    textAlign: "center",
    cursor: "pointer",
    boxShadow: `0px 2px 5px ${colors.SECONDARY}`,
  },
  avatar:{
    width: "100%",
    height: 180,
    display: "flex",
    flexDirection: "column",
    boxShadow: 3,
    border: "2px dashed #B5B5B0",
    fontSize: 14,
    background: "none",
    color: "inherit",
    fontWeight: "bold",
  },
  docPreview: {
    width: "100%",
    height: 180,
    objectFit: "cover",
    borderRadius: "10px",
    cursor: "zoom-in",
    boxShadow: "0px 2px 5px rgba(0,0,0,0.3)"
  }
}));

function UserDocuments() {
  const { id, rId } = useParams();
  const { t, i18n } = useTranslation();
  const isRTL = i18n.dir();
  const navigate = useNavigate();
  const { fetchUsersOnce, updateLicenseImage, editUser } = api;
  const [data, setData] = useState([]);
  const staticusers = useSelector((state) => state.usersdata.staticusers);
  const dispatch = useDispatch();
  
  const [editable, setEditable] = useState(false);
  
  const [docs, setDocs] = useState({
      verifyIdImage: null,
      verifyIdImageBack: null,
      licenseImage: null,
      licenseImageBack: null,
      photoWithLicense: null,
      policeClearance: null,
  });

  const [commonAlert, setCommonAlert] = useState({ open: false, msg: "" });
  const [loading, setLoading] = useState(false);
  const settings = useSelector(state => state.settingsdata.settings);
  const classes = useStyles();
  const {state} = useLocation();

  const [zoomImage, setZoomImage] = useState(null);

  const auth = useSelector((state) => state.auth);
  const targetId = id ? id.toString() : (auth.profile ? auth.profile.uid : null);

  useEffect(() => {
    dispatch(fetchUsersOnce());
  }, [dispatch, fetchUsersOnce]);

  useEffect(() => {
    if (targetId) {
      if (auth.profile && auth.profile.uid === targetId) {
        setData(auth.profile);
      } else if (staticusers) {
        const user = staticusers.find((user) => user.id === targetId);
        if (user) {
          setData(user);
        } else if (id) {
          navigate("/404");
        }
      }
    } else {
      setData([]);
    }
  }, [staticusers, targetId, auth.profile, id, navigate]);

  const handleImageChange = (e, field) => {
    if(e.target.files[0]) {
        setDocs({...docs, [field]: e.target.files[0]});
    }
  };

  const handleCommonAlertClose = (e) => {
    e.preventDefault();
    setCommonAlert({ open: false, msg: "" });
  };

  const handleSaveUser = async () => {
    setLoading(true);
    if(settings.AllowCriticalEditsAdmin || (auth.profile && auth.profile.usertype === 'driver')){
        let updates = [];
        const userId = data.id || targetId;
        
        if (docs.verifyIdImage) updates.push(dispatch(updateLicenseImage(userId, docs.verifyIdImage, "verifyIdImage")));
        if (docs.verifyIdImageBack) updates.push(dispatch(updateLicenseImage(userId, docs.verifyIdImageBack, "verifyIdImageBack")));
        if (docs.licenseImage) updates.push(dispatch(updateLicenseImage(userId, docs.licenseImage, "licenseImage")));
        if (docs.licenseImageBack) updates.push(dispatch(updateLicenseImage(userId, docs.licenseImageBack, "licenseImageBack")));
        if (docs.photoWithLicense) updates.push(dispatch(updateLicenseImage(userId, docs.photoWithLicense, "photoWithLicense")));
        if (docs.policeClearance) updates.push(dispatch(updateLicenseImage(userId, docs.policeClearance, "policeClearance")));

        if (updates.length > 0) {
            updates.push(dispatch(editUser(userId, { hasNewDocUpload: true, docUpdated: true })));
        }

        await Promise.all(updates);
        dispatch(fetchUsersOnce());
        
        setDocs({
            verifyIdImage: null,
            verifyIdImageBack: null,
            licenseImage: null,
            licenseImageBack: null,
            photoWithLicense: null,
            policeClearance: null,
        });
        setLoading(false);
        setEditable(false);
    }else{
      setLoading(false);
      setCommonAlert({ open: true, msg: t("demo_mode") });
    }
  };

  const handleCancel = () => {
    setDocs({
        verifyIdImage: null,
        verifyIdImageBack: null,
        licenseImage: null,
        licenseImageBack: null,
        photoWithLicense: null,
        policeClearance: null,
    });
    setEditable(false);
  };

  const [imgErrors, setImgErrors] = useState({});

  const handleImgError = (field) => {
    setImgErrors(prev => ({ ...prev, [field]: true }));
  };

  const renderSlot = (titleKey, field, urlField) => {
    const fileObj = docs[field];
    const serverUrl = data?.[urlField];
    const previewUrl = fileObj ? URL.createObjectURL(fileObj) : serverUrl;
    
    // Check if the current file is a PDF (either newly uploaded, has .pdf extension, or failed to load as an image)
    const isPdf = (fileObj && fileObj.type === 'application/pdf') || (serverUrl && serverUrl.toLowerCase().includes('.pdf')) || imgErrors[field];

    const previewContent = isPdf ? (
        <div style={{ width: '100%', height: 180, border: '2px dashed #ccc', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', backgroundColor: '#f5f5f5', borderRadius: 10 }}>
            <Typography variant="h4" color="textSecondary">PDF</Typography>
            <Typography variant="caption" color="textSecondary">{t('document')}</Typography>
        </div>
    ) : (
        <img src={previewUrl} className={classes.docPreview} alt={t(titleKey)} onError={() => handleImgError(field)} />
    );

    return (
        <Grid item xs={12} style={{ marginBottom: 20 }}>
            <Typography style={{ fontWeight: 'bold', marginBottom: 5 }}>{t(titleKey)}</Typography>
            {editable ? (
                <>
                    <input
                        accept="image/*,application/pdf"
                        style={{ display: 'none' }}
                        id={`upload-${field}`}
                        type="file"
                        onChange={(e) => {
                            setImgErrors(prev => ({ ...prev, [field]: false })); // Reset error state on new upload
                            handleImageChange(e, field);
                        }}
                    />
                    <label htmlFor={`upload-${field}`} style={{ cursor: 'pointer', display: 'block' }}>
                        {previewUrl ? previewContent : (
                            <Avatar className={classes.avatar} variant="square">
                                <FileUploadIcon sx={{ fontSize: 50, marginBottom: 1, color: "grey" }} />
                                {t('upload')}
                            </Avatar>
                        )}
                    </label>
                </>
            ) : (
                previewUrl ? (
                    <div onClick={() => isPdf ? window.open(previewUrl, '_blank') : setZoomImage(previewUrl)} style={{ cursor: isPdf ? 'pointer' : 'zoom-in' }}>
                        {previewContent}
                    </div>
                ) : (
                    <div style={{ width: '100%', height: 180, border: '2px dashed #ccc', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Typography color="textSecondary">{t('no_document')}</Typography>
                    </div>
                )
            )}
        </Grid>
    );
  };

  const isDriverApproved = auth.profile?.usertype === 'driver' && (data?.approved === true || auth.profile?.approved === true);

  return loading ? (
    <CircularLoading />
  ) : (
    <>
      <div>
        <Card style={{ borderRadius: "19px", backgroundColor: "#fff", minHeight: 200, marginTop: 20, marginBottom: 20, padding: 20 }}>
          <div style={{ display: "flex", flexDirection: "column", justifyContent: isRTL === "rtl" ? "flex-end" : "flex-start" }}>
            <Typography variant="h5" style={{ margin: "10px 10px 0 5px" }}>
              {t("documents_title")}
            </Typography>
            {rId ? (
              <div style={{ display: "flex" }}>
                <Button variant="text" onClick={() => navigate(`/users/${rId}`, { state: { pageNo: state?.pageNo }} ) }>
                  <Typography style={{ margin: "10px 10px 0 5px", textAlign: isRTL === "rtl" ? "right" : "left", fontWeight: "bold", color:colors.MAIN_COLOR }}>
                    {`<<- ${t("go_back")}`}
                  </Typography>
                </Button>
              </div>
            ) : null}
          </div>

          <Grid container spacing={3} style={{ marginTop: 20 }}>
              {/* Block 1: Identification */}
              <Grid item xs={12} sm={4}>
                  <Card style={{ padding: 15, height: '100%', backgroundColor: '#f9f9f9', boxShadow: 'none', border: '1px solid #eee' }}>
                      <Typography variant="h6" align="center" style={{ marginBottom: 15 }}>{t('identification_card')}</Typography>
                      {renderSlot('upload_verifyIdImage_front', 'verifyIdImage', 'verifyIdImage')}
                      {renderSlot('upload_verifyIdImage_back', 'verifyIdImageBack', 'verifyIdImageBack')}
                  </Card>
              </Grid>

              {/* Block 2: Driver's License */}
              {(data?.usertype === 'driver' || data?.licenseImage || data?.licenseImageBack || data?.photoWithLicense || data?.policeClearance) && (
                  <Grid item xs={12} sm={4}>
                      <Card style={{ padding: 15, height: '100%', backgroundColor: '#f9f9f9', boxShadow: 'none', border: '1px solid #eee' }}>
                          <Typography variant="h6" align="center" style={{ marginBottom: 15 }}>{t('drivers_license')}</Typography>
                          {renderSlot('upload_driving_license_front', 'licenseImage', 'licenseImage')}
                          {renderSlot('upload_driving_license_back', 'licenseImageBack', 'licenseImageBack')}
                      </Card>
                  </Grid>
              )}

              {/* Block 3: Security */}
              {(data?.usertype === 'driver' || data?.licenseImage || data?.licenseImageBack || data?.photoWithLicense || data?.policeClearance) && (
                  <Grid item xs={12} sm={4}>
                      <Card style={{ padding: 15, height: '100%', backgroundColor: '#f9f9f9', boxShadow: 'none', border: '1px solid #eee' }}>
                          <Typography variant="h6" align="center" style={{ marginBottom: 15 }}>{t('security')}</Typography>
                          {renderSlot('upload_photo_with_license', 'photoWithLicense', 'photoWithLicense')}
                          {renderSlot('upload_police_clearance', 'policeClearance', 'policeClearance')}
                      </Card>
                  </Grid>
              )}
          </Grid>

          <div style={{ display: 'flex', justifyContent: 'center', gap: 20, marginTop: 30 }}>
              {isDriverApproved ? (
                  <div style={{ textAlign: 'center', backgroundColor: '#e8f5e9', border: '1px solid #c8e6c9', padding: '12px 24px', borderRadius: 10 }}>
                      <Typography variant="body1" style={{ color: '#2e7d32', fontWeight: 'bold' }}>
                          {t('approved_doc_locked') || "Your account has been approved. Documents cannot be modified."}
                      </Typography>
                  </div>
              ) : !editable ? (
                  <Button variant="contained" style={{ backgroundColor: colors.MAIN_COLOR, color: '#fff', borderRadius: 20, padding: '10px 40px' }} onClick={() => setEditable(true)}>
                      {t('edit')}
                  </Button>
              ) : (
                  <>
                      <Button variant="contained" style={{ backgroundColor: colors.MAIN_COLOR, color: '#fff', borderRadius: 20, padding: '10px 40px' }} onClick={handleSaveUser}>
                          {t('save')}
                      </Button>
                      <Button variant="outlined" style={{ borderRadius: 20, padding: '10px 40px' }} onClick={handleCancel}>
                          {t('cancel')}
                      </Button>
                  </>
              )}
          </div>
        </Card>
      </div>

      <ImageViewerModal
        open={!!zoomImage}
        src={zoomImage}
        title={t('document_preview', 'Document Preview')}
        onClose={() => setZoomImage(null)}
      />

      <AlertDialog open={commonAlert.open} onClose={handleCommonAlertClose}>{commonAlert.msg}</AlertDialog>
    </>
  );
}

export default UserDocuments;
